/**
 * Queue 1 envelopes (SM_AppointmentTransactions). One typed function per operation; each returns the
 * exact field set `SM.envelope` in reference/sm_live_harness.js writes — every required column with a
 * default written explicitly — plus the operation's own fields. The app asks; the processor decides.
 *
 * Keys: `TransactionID`, `IdempotencyKey` and `CorrelationID` belong to a **form session** (newFormSession
 * when a form opens; a new one when it is reopened after a rejection). Pressing Book twice in the same
 * session sends the same IdempotencyKey, which the list itself refuses — "already in the queue".
 * A press that submits several envelopes in order (a displacement) derives one key set per step.
 *
 * No dependencies beyond the shared vocabulary, so the app and the API both use it.
 */
import { vocab } from '../vocab';
import type { Role } from '../roles';
import { keyToNoonUtc } from '../dates';

const V = vocab.SM_AppointmentTransactions;
type Of<K extends keyof typeof V> = (typeof V)[K][number];

export type Q1Operation = Of<'OperationType'>;
export type CancellationInitiatedBy = Of<'CancellationInitiatedBy'>;
export type OutcomeReasonCategory = Of<'OutcomeReasonCategory'>;
export type TenantResponseOutcome = Of<'TenantResponseOutcome'>;
export type TenantResponseMethod = Of<'TenantResponseMethod'>;
export type ServicePeriod = Of<'RequestedServicePeriod'>;
export type PriorStatus = Of<'PriorStatusSnapshot'>;
export type ReservationDisposition = Of<'RequestedReservationDisposition'>;

export const Q1_LIST = 'SM_AppointmentTransactions';
/** RequestSnapshotVersion the harness writes and the processor accepts. */
export const Q1_PAYLOAD_VERSION = '1.0';
/** The version of the app's own RequestSnapshotJSON shape (inside the JSON). */
export const Q1_SNAPSHOT_SCHEMA = 'abf-web/q1@1';
/** The transaction list's vocabulary is Power App · Scheduled Flow · Admin Manual · Migration; the web app is the App. */
export const Q1_CREATION_SOURCE: Of<'CreationSource'> = 'Power App';
export const HOUSEKEEPING = 'HOUSEKEEPING';

/** The states in which a transaction is still being worked on. */
export const OPEN_PROCESSING = ['Pending', 'Claimed', 'In Progress'] as const;
export const isOpenProcessing = (s: string | null | undefined): boolean => (OPEN_PROCESSING as readonly string[]).includes(String(s ?? ''));

export interface FormSession {
  transactionId: string;
  idempotencyKey: string;
  correlationId: string;
}

function rand(n: number): string {
  const c = globalThis.crypto;
  const hex = c?.randomUUID ? c.randomUUID().replace(/-/g, '') : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return hex.slice(0, n).toUpperCase();
}

/** yyyyMMddHHmmss (UTC) */
function stamp(now: Date): string {
  return now.toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
}

/** A fresh key set for a form that just opened (or reopened after a rejection). */
export function newFormSession(now: Date = new Date()): FormSession {
  const s = `${stamp(now)}-${rand(10)}`;
  return { transactionId: `TXN-${s}`, idempotencyKey: `IDEM-${s}`, correlationId: `CORR-${s}` };
}

/** Step n (1-based) of a press that submits several envelopes: its own keys, derived from the session. */
export function sessionStep(session: FormSession, step: number): FormSession {
  if (step <= 1) return session;
  const sfx = `-S${step}`;
  return {
    transactionId: session.transactionId + sfx,
    idempotencyKey: session.idempotencyKey + sfx,
    correlationId: session.correlationId + sfx,
  };
}

export const SESSION_KEY_RE = /^(TXN|IDEM|CORR)-\d{14}-[0-9A-F]{10}(-S\d{1,2})?$/;

/** RequestedRoleContext from the app role. The queue has no co-administrator value: a co-administrator
 * schedules as Scheduling and Program Staff (docs/DECISIONS.md D-0.3.0-3). */
export function roleContext(role: Role | 'tenant'): Of<'RequestedRoleContext'> {
  if (role === 'tenant') return 'Tenant Self-Service';
  return role === 'admin' ? 'Administrator' : 'Scheduling and Program Staff';
}

/**
 * Who pressed. `tenant` is a tenant's own press on their signed link (prompt 0.6.0): the envelope carries
 * their email and the role context "Tenant Self-Service" (docs/SCHEMA-V4-ADDITIONS.md §5).
 */
export interface Requester {
  email: string;
  role: Role | 'tenant';
}

/** Who and what the request is about — every operation carries the tenant (TargetTenantID is required). */
export interface Target {
  tenantId: string;
  enrollmentId?: string | null;
  programCode?: string;
}

export type Q1Envelope = Record<string, string | number | boolean | null>;

interface BaseArgs {
  op: Q1Operation;
  session: FormSession;
  by: Requester;
  target: Target;
  /** The form's values, recorded as they were pressed (RequestSnapshotJSON). */
  form: Record<string, unknown>;
  now?: Date;
}

/** The SM.envelope field set: every required column, defaults written explicitly. */
function base(a: BaseArgs): Q1Envelope {
  const now = a.now ?? new Date();
  return {
    Title: `${a.op} · ${a.target.tenantId}`.slice(0, 255),
    TransactionID: a.session.transactionId,
    IdempotencyKey: a.session.idempotencyKey,
    CorrelationID: a.session.correlationId,
    SubmittedDateTime: now.toISOString(),
    CreationSource: Q1_CREATION_SOURCE,
    RequestedByEmailSnapshot: a.by.email,
    RequestedRoleContext: roleContext(a.by.role),
    RecordClass: 'Staff Operation',
    OperationType: a.op,
    ProcessorAction: 'Not Applicable',
    ProcessingStatus: 'Pending',
    ProcessingStage: 'Not Started',
    PostCommitStatus: 'Not Applicable',
    CompensationStatus: 'Not Required',
    RecoveryStatus: 'Not Required',
    ProcessingAttempts: 0,
    OutcomeIsRetryable: false,
    RequestSnapshotJSON: JSON.stringify({ schema: Q1_SNAPSHOT_SCHEMA, version: Q1_PAYLOAD_VERSION, operation: a.op, submittedBy: a.by.email, form: a.form }),
    RequestSnapshotVersion: Q1_PAYLOAD_VERSION,
    ExpectedCommunicationSetSnapshot: 'None',
    TargetTenantID: a.target.tenantId,
    TargetEnrollmentID: a.target.enrollmentId ?? null,
    TargetProgramCode: a.target.programCode ?? HOUSEKEEPING,
    RequestedServicePeriod: 'Morning',
    RequestedReservationDisposition: 'Not Applicable',
    ResultingReservationDisposition: 'Not Applicable',
    PriorStatusSnapshot: 'Not Applicable',
    TenantResponseOutcome: 'Not Applicable',
    TenantResponseMethod: 'Not Applicable',
    VolunteerResponseOutcome: 'Not Applicable',
    OutcomeReasonCategory: 'Not Applicable',
    CancellationInitiatedBy: 'Not Applicable',
    AuthorizationExceptionRequested: false,
    AuthorizationExceptionStatus: 'Not Required',
  };
}

/* ------------------------------------------------------------------------------------------------
 * The operations. Each takes what the form knows plus the session and the requester, and returns the
 * full envelope. Fields the processor reads per operation (reference/definition_body.json):
 *   Create        TargetTenantID, TargetEnrollmentID, TargetServiceDateKey, RequestedServicePeriod,
 *                 ProposalBatchID (pencil), IdempotencyKey (= the new AppointmentGenerationKey)
 *   Reschedule    TargetAppointmentID, TargetServiceDateKey (the new day; the same day only with a picked position — v8j, the crew's "come back later today"),
 *                 RequestedServicePeriod, IdempotencyKey (= the successor's generation key)
 *   Cancel        TargetAppointmentID, CancellationInitiatedBy, OutcomeReasonCategory (both required
 *                 by the list's own validation — never Not Applicable)
 *   Tenant answer TargetAppointmentID, TenantResponseOutcome, TenantResponseMethod
 *   Hold          TargetAppointmentID, RequestedReservationDisposition, HoldUntil (Retain: ≤ submitted + 48 h;
 *                 Release Immediately: empty)
 *   Extend        TargetAppointmentID, HoldUntil (later than now, ≤ the Place's submitted + 48 h)
 *   Release / Start / No Show / Unable To Complete   TargetAppointmentID
 *   Reopen        TargetAppointmentID, OutcomeReasonCategory (non-empty), ReopenReason
 * RequestedCapacityPosition (1.4.0, Queue 1 v8j): Create and Reschedule may name the position ("pick a
 * spot"); v8j books exactly that position when it is free and refuses with the free ones named when it is
 * not; empty = the first free position, as v8i always does. The app sends it only while the setting
 * HK_PICK_SPOT_ENABLED is yes (Elijah turns it on after importing v8j).
 * ---------------------------------------------------------------------------------------------- */

export interface Common {
  session: FormSession;
  by: Requester;
  now?: Date;
}

/** An existing appointment the operation acts on. */
export interface ApptTarget extends Target {
  appointmentId: string;
  /** Its civil day (yyyyMMdd) — recorded as TargetServiceDateKey so the attention strip can place it. */
  dayKey?: string | null;
  /** Its status now — PriorStatusSnapshot (the processor re-reads it; this is the record of what we saw). */
  priorStatus?: string | null;
}

function prior(s: string | null | undefined): PriorStatus {
  const v = V.PriorStatusSnapshot as readonly string[];
  return (s && v.includes(s) ? s : 'Not Applicable') as PriorStatus;
}

function lifecycle(op: Q1Operation, c: Common, t: ApptTarget, form: Record<string, unknown>, over: Q1Envelope = {}): Q1Envelope {
  return {
    ...base({ op, session: c.session, by: c.by, target: t, form: { appointmentId: t.appointmentId, ...form }, now: c.now }),
    TargetAppointmentID: t.appointmentId,
    TargetServiceDateKey: t.dayKey ?? null,
    RequestedServiceDate: t.dayKey ? keyToNoonUtc(t.dayKey) : null,
    PriorStatusSnapshot: prior(t.priorStatus ?? null),
    ...over,
  };
}

export interface CreateInput extends Common {
  target: Target;
  dayKey: string;
  period?: ServicePeriod;
  /** Pencil: a proposal (Planning, 0.7.0). A one-off staff booking is born ink (no batch). */
  proposalBatchId?: string | null;
  /** Everything else the form held (note for the crew, deep clean asked, the position shown, …). */
  form?: Record<string, unknown>;
  /** Pick a spot (v8j): the position asked for; null/absent = the first free one. */
  requestedPosition?: number | null;
}

const position = (p: number | null | undefined): Q1Envelope => (p && p > 0 ? { RequestedCapacityPosition: p } : {});

export function createAppointment(i: CreateInput): Q1Envelope {
  const period = i.period ?? 'Morning';
  return {
    ...base({ op: 'Create Appointment', session: i.session, by: i.by, target: i.target, form: { dayKey: i.dayKey, period, ...(i.form ?? {}) }, now: i.now }),
    TargetServiceDateKey: i.dayKey,
    RequestedServiceDate: keyToNoonUtc(i.dayKey),
    RequestedServicePeriod: period,
    RequestedReservationDisposition: 'Acquire',
    ProposalBatchID: i.proposalBatchId ?? null,
    ...position(i.requestedPosition),
  };
}

/** Who asked for a reschedule: the tenant, or the office (with a reason and a note). */
export type RequestedBy = 'tenant' | 'office';

export interface RescheduleInput extends Common {
  target: ApptTarget;
  newDayKey: string;
  period?: ServicePeriod;
  requestedBy: RequestedBy;
  /** For the office: why (from the vocabulary). The tenant's own request is Tenant Unavailable. */
  reason?: OutcomeReasonCategory;
  note?: string;
  form?: Record<string, unknown>;
  /** Pick a spot (v8j): the position on the new day; null/absent = the first free one. */
  requestedPosition?: number | null;
}

/**
 * Reschedule. The queue has no reschedule-reason column; who asked is written into the columns it
 * offers for who initiated a change and why (CancellationInitiatedBy Tenant/Staff, OutcomeReasonCategory
 * — the processor does not read them for a reschedule, they are the record) and into RequestSnapshotJSON
 * as `requestedBy` (docs/DECISIONS.md D-0.3.0-6).
 */
export function rescheduleAppointment(i: RescheduleInput): Q1Envelope {
  const period = i.period ?? 'Morning';
  const reason: OutcomeReasonCategory = i.requestedBy === 'tenant' ? 'Tenant Unavailable' : i.reason && i.reason !== 'Not Applicable' ? i.reason : 'Other';
  return lifecycle(
    'Reschedule Appointment',
    i,
    i.target,
    { newDayKey: i.newDayKey, period, requestedBy: i.requestedBy, reason, note: i.note ?? '', ...(i.form ?? {}) },
    {
      TargetServiceDateKey: i.newDayKey,
      RequestedServiceDate: keyToNoonUtc(i.newDayKey),
      RequestedServicePeriod: period,
      CancellationInitiatedBy: i.requestedBy === 'tenant' ? 'Tenant' : 'Staff',
      OutcomeReasonCategory: reason,
      ...position(i.requestedPosition),
    },
  );
}

export type CancelInitiator = Exclude<CancellationInitiatedBy, 'Not Applicable'>;
export type ReasonCategory = Exclude<OutcomeReasonCategory, 'Not Applicable'>;

export interface CancelInput extends Common {
  target: ApptTarget;
  initiatedBy: CancelInitiator;
  reason: ReasonCategory;
  note?: string;
  form?: Record<string, unknown>;
}

export function cancelAppointment(i: CancelInput): Q1Envelope {
  return lifecycle('Cancel Appointment', i, i.target, { initiatedBy: i.initiatedBy, reason: i.reason, note: i.note ?? '', ...(i.form ?? {}) }, {
    CancellationInitiatedBy: i.initiatedBy,
    OutcomeReasonCategory: i.reason,
  });
}

export interface TenantAnswerInput extends Common {
  target: ApptTarget;
  outcome: Exclude<TenantResponseOutcome, 'Not Applicable'>;
  method: Exclude<TenantResponseMethod, 'Not Applicable'>;
  note?: string;
  /** Anything else the press held (1.5.13: "confirmed before the app", the reason, the run) — the record only. */
  form?: Record<string, unknown>;
}

export function recordTenantResponse(i: TenantAnswerInput): Q1Envelope {
  return lifecycle('Record Tenant Confirmation Response', i, i.target, { outcome: i.outcome, method: i.method, note: i.note ?? '', ...(i.form ?? {}) }, {
    TenantResponseOutcome: i.outcome,
    TenantResponseMethod: i.method,
  });
}

/** The longest a hold keeps the slot (D-P11): the processor refuses a Retain past submitted + 48 h. */
export const HOLD_MAX_HOURS = 48;

/**
 * A HoldUntil the processor will accept: no later than `latest` (submitted + 48 h for a Place; the
 * Place's submitted + 48 h for an Extend), rounded down to the minute (SharePoint keeps DateTime to the
 * second, so a value with milliseconds past the limit is refused).
 */
export function clampHoldUntil(requested: Date, latest: Date): string {
  const t = Math.min(requested.getTime(), latest.getTime());
  return new Date(Math.floor(t / 60_000) * 60_000).toISOString().replace('.000Z', 'Z');
}

/** Submitted (to the second) + 48 h. */
export function holdLimitFrom(submitted: Date): Date {
  return new Date(Math.floor(submitted.getTime() / 1000) * 1000 + HOLD_MAX_HOURS * 3600_000);
}

export type HoldDisposition = 'Retain Until Hold Until' | 'Release Immediately';

export interface HoldInput extends Common {
  target: ApptTarget;
  disposition: HoldDisposition;
  /** Retain only: the ISO instant the slot is kept until (clamped to the 48-hour limit). */
  holdUntil?: string | null;
  note?: string;
}

export function placeOnHold(i: HoldInput): Q1Envelope {
  const now = new Date(Math.floor((i.now ?? new Date()).getTime() / 1000) * 1000);
  const until =
    i.disposition === 'Retain Until Hold Until' ? clampHoldUntil(i.holdUntil ? new Date(i.holdUntil) : holdLimitFrom(now), holdLimitFrom(now)) : null;
  return lifecycle('Place Appointment On Hold', { ...i, now }, i.target, { disposition: i.disposition, holdUntil: until, note: i.note ?? '' }, {
    RequestedReservationDisposition: i.disposition,
    HoldUntil: until,
  });
}

export interface ExtendInput extends Common {
  target: ApptTarget;
  holdUntil: string;
  /** When the hold was placed (the Place row's SubmittedDateTime): an extension ends by then + 48 h. */
  placedAt: string;
}

export function extendHold(i: ExtendInput): Q1Envelope {
  const until = clampHoldUntil(new Date(i.holdUntil), holdLimitFrom(new Date(i.placedAt)));
  return lifecycle('Extend Appointment Hold', i, i.target, { holdUntil: until, placedAt: i.placedAt }, {
    RequestedReservationDisposition: 'Retain Until Hold Until',
    HoldUntil: until,
  });
}

export function releaseHold(i: Common & { target: ApptTarget }): Q1Envelope {
  return lifecycle('Release Appointment Hold', i, i.target, {});
}

export function startService(i: Common & { target: ApptTarget }): Q1Envelope {
  return lifecycle('Start Service', i, i.target, {});
}

export function recordNoShow(i: Common & { target: ApptTarget; note?: string }): Q1Envelope {
  return lifecycle('Record No Show', i, i.target, { note: i.note ?? '' });
}

export function recordUnableToComplete(i: Common & { target: ApptTarget; reason?: ReasonCategory; note?: string }): Q1Envelope {
  return lifecycle('Record Unable To Complete', i, i.target, { reason: i.reason ?? null, note: i.note ?? '' }, i.reason ? { OutcomeReasonCategory: i.reason } : {});
}

export interface ReopenInput extends Common {
  target: ApptTarget;
  reason: string;
  category: ReasonCategory;
}

export function reopenAppointment(i: ReopenInput): Q1Envelope {
  return lifecycle('Reopen Appointment', i, i.target, { reason: i.reason, category: i.category }, {
    ReopenReason: i.reason,
    OutcomeReasonCategory: i.category,
  });
}

/**
 * The cancellation a displacement writes for a Scheduled line still waiting for the tenant's answer.
 * The vocabulary has no "rescheduling" reason; Scheduling Error is the one that says the office moved
 * the line (docs/DECISIONS.md D-0.3.0-5).
 */
export const DISPLACEMENT_CANCEL = { initiatedBy: 'Staff', reason: 'Scheduling Error' } as const satisfies {
  initiatedBy: CancelInitiator;
  reason: ReasonCategory;
};
